import styled, { css } from 'styled-components';

import { GraphQLCampaign } from 'modules/campaignIndex/domainTypes';
import { CampaignState, CampaignType, MessageMedium } from 'modules/domains/campaign';
import { usePreviewThumbnails } from 'modules/templateIndex/hooks';
import { Badge } from 'ui/badge';
import { SLIDE_OVER_WIDTH } from 'ui/slideOver/SlideOver.styled';
import { caption, colors, fontSizes, heading5, spacing } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import { useIsOverflowing } from 'utils/hooks/useIsOverflowing';

import { CAMPAIGN_METADATA_ICON_SIZE } from '../../constants';
import {
  CAMPAIGN_TYPE_CONTENT_MAPPER,
  MESSAGE_MEDIUM_CONTENT_MAPPER,
  getCampaignStateBadgeVariant,
} from '../../utils';
import { CampaignPreviewThumbnail } from '../CampaignPreviewThumbnail';

const ellipsisText = css`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin: 0;
  padding: 0;
  overflow-wrap: anywhere;
`;

const HeaderCampaign = styled.div`
  display: inline-flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${spacing.base};
  color: ${colors.textPrimary};
  width: calc(${SLIDE_OVER_WIDTH} - 98px);
`;

const CampaignName = styled.span`
  ${heading5}
  ${ellipsisText}
  margin-top: -3px; /** This aligns the text to the top as we are not able to use vertical-align here */
`;

const CampaignContent = styled.span`
  display: flex;
  gap: ${spacing.half};
  flex-direction: column;
`;

const CampaignPreview = styled.div`
  min-width: 56px;
`;

const CampaignDetails = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  display: flex;
  align-items: center;
  justify-content: flex-start;

  white-space: nowrap;
  font-size: ${fontSizes.s12};
  line-height: 1.15em;
  color: ${colors.iconPrimary};
`;

const StyledTooltip = styled(Tooltip)`
  ${ellipsisText}
`;

const CampaignDetailsItem = styled.li`
  ${caption}
  display: inline-flex;
  align-items: center;
  & > :first-child {
    margin-right: ${spacing.quarter};
  }

  &:not(:last-child):after {
    padding-inline: ${spacing.half};
    content: '•';
    color: ${colors.textDisabled};
  }

  &:last-child {
    padding-inline: 0;
  }
`;

type AlertCampaignProps = {
  name: string;
  campaignId: number;
  templateId: number;
  messageMedium: MessageMedium;
  campaignType: CampaignType;
  campaignState: CampaignState;
};

export const AlertCampaign = ({
  name,
  campaignId,
  templateId,
  messageMedium,
  campaignType,
  campaignState,
}: AlertCampaignProps) => {
  const { Icon: MessageIcon, displayName: messageDisplayName } =
    MESSAGE_MEDIUM_CONTENT_MAPPER[messageMedium] || {};
  const { Icon: CampaignTypeIcon, displayName: campaignDisplayName } =
    CAMPAIGN_TYPE_CONTENT_MAPPER[campaignType] || {};
  const badgeVariant = getCampaignStateBadgeVariant(campaignState);
  const previewThumbnail = usePreviewThumbnails([templateId])[templateId];
  const { isOverflowing, elementRef } = useIsOverflowing<HTMLSpanElement>({
    overflowX: false,
    overflowY: true,
    normalizeOffset: 1,
  });

  return (
    <HeaderCampaign>
      <CampaignPreview>
        <CampaignPreviewThumbnail
          campaign={
            {
              templateId,
              id: campaignId,
              campaignState,
              messageMedium,
            } as unknown as GraphQLCampaign
          }
          previewThumbnail={previewThumbnail}
        />
      </CampaignPreview>

      <CampaignContent>
        <StyledTooltip tooltipContent={name} isEnabled={isOverflowing}>
          <CampaignName ref={elementRef}>{name}</CampaignName>
        </StyledTooltip>

        <CampaignDetails>
          <CampaignDetailsItem>
            <MessageIcon {...CAMPAIGN_METADATA_ICON_SIZE} color={colors.iconPrimary} />
            {messageDisplayName}
          </CampaignDetailsItem>

          <CampaignDetailsItem>
            <CampaignTypeIcon {...CAMPAIGN_METADATA_ICON_SIZE} color={colors.iconPrimary} />
            {campaignDisplayName}
          </CampaignDetailsItem>

          <CampaignDetailsItem>
            <Badge variant={badgeVariant}>{campaignState}</Badge>
          </CampaignDetailsItem>
        </CampaignDetails>
      </CampaignContent>
    </HeaderCampaign>
  );
};
